import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgModel } from '@angular/forms';
import * as _ from 'lodash-es';
import { MessagingService } from 'src/app/core/messaging/messaging.service';
import { TranslationService } from 'src/app/core/translation/translation.service';
import { ButtonType } from 'src/app/shared/common/components/buttons/button';
import { FeaturesStreamsService } from 'src/app/shared/common/current-features/features-streams.service';
import { TaskFeaturesStreamsService } from 'src/app/shared/common/current-features/task-features-streams.service';
import { TasksStore } from 'src/app/shared/common/current-tasks/tasks-store.service';
import { TasksStreamsService } from 'src/app/shared/common/current-tasks/tasks-streams.service';
import { Task, TaskCreationMode } from 'src/app/shared/common/task/task';
import { CloneUtils } from 'src/app/shared/common/utility/clone-utils';
import { Feature } from 'src/app/shared/map-data-services/feature/feature';
import { Actor } from 'src/app/shared/user/actor';

@Component({
    selector: 'task-creation-panel',
    templateUrl: './task-creation-panel.component.html'
})
export class TaskCreationPanelComponent implements OnInit, OnDestroy {
    @Input()
    task: Task;

    public originalTask: Task = null;
    public currentProjectAssignees: Actor[] = [];
    public taskCreationMode = TaskCreationMode.NEW;
    public loading = true;

    public headerText = {
        NEW: this.translate.instant('TC.Common.NewTask'),
        NEW_FROM_SELECTION: this.translate.instant('TC.Common.NewTask'),
        EDIT: this.translate.instant('TC.Common.EditTask')
    };

    public formsSelectionPanelActive = false;
    public formSelectionDisableDone = true;
    public previousLayerToFeaturesMapUnset = true;
    public layerToFeaturesMap: Map<string, Feature[]> = new Map();
    private previousLayerToFeaturesMap: Map<string, Feature[]>;

    ButtonType = ButtonType;
    TaskCreationMode = TaskCreationMode;

    constructor(
        private tasksStore: TasksStore,
        private tasksStreams: TasksStreamsService,
        private featuresStreams: FeaturesStreamsService,
        private messaging: MessagingService,
        private translateService: TranslationService,
        private taskFeaturesStreams: TaskFeaturesStreamsService,
        private translate: TranslationService,
        private cdr: ChangeDetectorRef
    ) {}

    async ngOnInit() {
        if (this.task.id) {
            this.taskFeaturesStreams.taskSelectedFeaturesStream.next(this.task.taskFeatures);
            this.taskCreationMode = TaskCreationMode.EDIT;
        } else {
            this.task = new Task();
            if (this.taskFeaturesStreams.taskSelectedFeaturesStream.getValue().length) {
                this.taskCreationMode = TaskCreationMode.NEW_FROM_SELECTION;
            }
        }
        this.originalTask = CloneUtils.cloneDeep(this.task);
        this.currentProjectAssignees = this.tasksStore.getAllAssignees();
        this.featuresStreams.selectedFeaturesStream.next([]);
        this.loading = false;
    }

    private needsSave(): boolean {
        return (
            this.task.title !== this.originalTask.title ||
            this.task.description !== this.originalTask.description ||
            !_.isEqual(this.task.assignees, this.originalTask.assignees)
        );
    }

    public get exceedsLimit(): boolean {
        return this.task && this.task.features && this.task.features.length > 1000;
    }

    public getErrorText(title: NgModel): string {
        if (title.touched) {
            const isRequiredError = _.filter([title.errors], 'required');

            if (isRequiredError && isRequiredError.length) {
                return this.translate.instant('Common.ValueRequired.Tooltip');
            }
        }

        return null;
    }

    public toggleFormsSelectionPanel(revertSelection?: boolean): void {
        if (this.formsSelectionPanelActive) {
            this.featuresStreams.clearSelectedFeatures();
            if (revertSelection) {
                this.layerToFeaturesMap = this.previousLayerToFeaturesMap;
                const previousFeatures = Array.from(this.previousLayerToFeaturesMap.values()).flat();
                this.taskFeaturesStreams.taskSelectedFeaturesStream.next(previousFeatures);
                this.previousLayerToFeaturesMap = new Map();
            }
        } else {
            this.previousLayerToFeaturesMap = new Map();
        }

        this.formsSelectionPanelActive = !this.formsSelectionPanelActive;
        this.previousLayerToFeaturesMapUnset = true;

        this.tasksStreams.disableMapviewerStream.next(!this.formsSelectionPanelActive);
        this.tasksStreams.taskFeatureSelectionModeActiveStream.next(this.formsSelectionPanelActive);
    }

    public addVisibleFormsToSelection() {
        this.loading = true;
        this.taskFeaturesStreams.getVisibleTemplateLayerFeatures().then(features => {
            this.taskFeaturesStreams.taskSelectedFeaturesStream.next(features);
            this.loading = false;
        });
    }

    public onAssigneesChange(assignedUsers: Partial<Actor[]>): void {
        this.task.assignees = assignedUsers;
    }

    public setLayerToFeaturesMap(map: Map<string, Feature[]>) {
        this.layerToFeaturesMap = map;
        if (this.previousLayerToFeaturesMapUnset) {
            this.previousLayerToFeaturesMap = CloneUtils.cloneDeep(map);
            this.previousLayerToFeaturesMapUnset = false;
        }
        this.formSelectionDisableDone = _.isEqual(this.previousLayerToFeaturesMap, this.layerToFeaturesMap);
        // cdr to trigger change detection when the map is updated onDestroy from task-forms-selection
        this.cdr.detectChanges();
    }

    public checkTaskMode(mode: TaskCreationMode): boolean {
        return this.taskCreationMode === mode;
    }

    public back() {
        this.task = CloneUtils.cloneDeep(this.originalTask);
        if (
            this.taskCreationMode === TaskCreationMode.NEW ||
            this.taskCreationMode === TaskCreationMode.NEW_FROM_SELECTION
        ) {
            this.tasksStreams.setSelectedTasks([]);
            this.taskFeaturesStreams.taskSelectedFeaturesStream.next([]);
        } else if (this.taskCreationMode === TaskCreationMode.EDIT) {
            this.taskFeaturesStreams.taskSelectedFeaturesStream.next([]);
            this.tasksStreams.setSelectedTasks([this.task]);
        }
    }
    ngOnDestroy(): void {
        this.tasksStreams.disableMapviewerStream.next(false);
        this.tasksStreams.taskFeatureSelectionModeActiveStream.next(false);
        this.featuresStreams.clearSelectedFeatures();
        this.taskFeaturesStreams.taskSelectedFeaturesStream.next([]);
    }

    public cancelSave(): void {
        if (this.formsSelectionPanelActive) {
            this.toggleFormsSelectionPanel(true);
            return;
        }
        this.task = CloneUtils.cloneDeep(this.originalTask);
        this.back();
    }

    private canSave(): boolean {
        return !!this.task.title;
    }

    private saveTask(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            if (this.canSave()) {
                this.task.description =
                    this.task.description ||
                    this.translate.instant('TC.Common.CreatedOn') + ' ' + new Date().toLocaleDateString();
                this.tasksStore.saveTask(this.task).then(
                    savedTask => {
                        const toastr = this.messaging.showSuccessWithAction(
                            this.translate.instant(
                                this.taskCreationMode === TaskCreationMode.NEW
                                    ? 'TC.TodosTask.Message.Created'
                                    : 'TC.TodosTask.Message.Saved'
                            ),
                            null,
                            this.translate.instant('Common.Open').toUpperCase(),
                            { enableHtml: true }
                        );
                        toastr.onTap.subscribe(() => {
                            this.tasksStreams.selectedTasksStream.next([savedTask]);
                        });

                        this.originalTask = CloneUtils.cloneDeep(this.task);
                        resolve(Boolean(savedTask));
                    },
                    () => {
                        this.messaging.showError(
                            this.translate.instant(
                                this.taskCreationMode === TaskCreationMode.NEW
                                    ? 'TC.Common.ErrorWhileCreating'
                                    : 'TC.Common.ErrorWhileUpdating'
                            )
                        );
                        reject(false);
                    }
                );
            } else {
                reject('cannot save');
            }
        });
    }

    public saveAction = (): Promise<Task> => this.save();

    private save(): Promise<Task> {
        const currentFeatures = this.taskFeaturesStreams.taskSelectedFeaturesStream.getValue();
        this.formsSelectionPanelActive = false;

        if (this.taskCreationMode === TaskCreationMode.EDIT) {
            if (!_.isEqual(currentFeatures, this.task.taskFeatures)) {
                // Features have been removed while editing the task
                this.task.features = this.task.features.filter(feature =>
                    currentFeatures.some(featureInSelection => featureInSelection.id === feature.featureId)
                );
                this.task.totalFeatures = this.task.features.length;
            } else if (!this.needsSave()) {
                this.messaging.showInfo(this.translateService.instant('TC.Common.NoChangesMade'));
                this.back();
                return Promise.resolve(null);
            }
        } else {
            this.task.features = this.tasksStore.getTaskFeaturesFromSelectedFeatures(currentFeatures);
        }

        return this.saveTask().then(this.back.bind(this));
    }
}
